import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '../../components/ui/use-toast';
import { supabase } from '../../lib/supabase';
import { PAYPAL_API_URL } from '../../services/paymentService';
import { config } from '../../config/config';

export const PaymentCallback: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const payerId = params.get('PayerID');

    const capturePayment = async () => {
      try {
        // Obtenir un token d'accès
        const authResponse = await fetch(`${PAYPAL_API_URL}/v1/oauth2/token`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Accept-Language': 'en_US',
            'Authorization': `Basic ${btoa(`${config.paypalClientId}:${config.paypalSecret}`)}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: 'grant_type=client_credentials'
        });

        if (!authResponse.ok) {
          const authError = await authResponse.json();
          console.error('Erreur d\'authentification PayPal:', authError);
          throw new Error('Échec de l\'authentification PayPal');
        }

        const { access_token } = await authResponse.json();

        // Trouver d'abord la transaction
        const { data: transaction, error: fetchError } = await supabase
          .from('transactions')
          .select('*, contestants(contests(slug))')
          .eq('payment_reference', token)
          .single();

        if (fetchError || !transaction) {
          console.error('Erreur lors de la récupération de la transaction:', fetchError);
          throw new Error('Transaction non trouvée');
        }

        console.log('Transaction trouvée:', transaction);

        // Capturer le paiement
        const response = await fetch(`${PAYPAL_API_URL}/v2/checkout/orders/${token}/capture`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${access_token}`,
            'PayPal-Request-Id': transaction.id,
          }
        });

        const paymentData = await response.json();
        console.log('Réponse de capture PayPal:', paymentData);

        if (!response.ok) {
          console.error('Erreur de capture PayPal:', paymentData);
          throw new Error(paymentData.message || 'Échec de la capture du paiement');
        }

        if (paymentData.status === 'COMPLETED') {
          // Préparer les données de mise à jour
          const updateData = {
            status: 'completed',
            payment_data: paymentData,
            updated_at: new Date().toISOString(),
            phone_number: '237670000000' // Numéro par défaut pour PayPal
          };

          console.log('Données de mise à jour:', updateData);

          // Mettre à jour la transaction dans la base de données
          const { error: updateError } = await supabase
            .from('transactions')
            .update(updateData)
            .eq('payment_reference', token)
            .select();

          if (updateError) {
            console.error('Erreur lors de la mise à jour de la transaction:', updateError);
            throw new Error(`Erreur de mise à jour: ${updateError.message}`);
          }

          toast({
            title: "Paiement réussi !",
            description: "Vos votes ont été comptabilisés.",
            variant: "default",
          });

          // Rediriger vers la page du concours avec le slug
          const contestSlug = transaction.contestants?.contests?.slug;
          navigate(contestSlug ? `/contests/${contestSlug}` : '/contests');
        } else {
          console.error('Statut de paiement invalide:', paymentData.status);
          throw new Error('Le paiement n\'a pas été complété');
        }
      } catch (error) {
        console.error('Erreur lors de la capture du paiement:', error);
        toast({
          title: "Erreur de paiement",
          description: error instanceof Error ? error.message : "Une erreur est survenue",
          variant: "destructive",
        });
        navigate('/payment/error');
      }
    };

    if (token && payerId) {
      capturePayment();
    } else {
      console.error('Token ou PayerID manquant:', { token, payerId });
      navigate('/payment/error');
    }
  }, [location.search, navigate, toast]);

  return (
    <div className="min-h-screen bg-black/90 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4" />
        <p className="text-white/90">Traitement de votre paiement en cours...</p>
      </div>
    </div>
  );
};
